import { useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { measureTextWidth } from "utils/functions";
import useChartWidth from "hooks/useChartWidth";

type Percentage = number & { __brand: "Percentage" };
type Unit = string | ((value: number) => string);

export function asPercentage(value: number): Percentage {
    if (value < 0 || value > 1) {
        throw new Error(`Percentage must be between 0 and 1`);
    }
    return value as Percentage;
}

function lerpHexColor(hex1: string, hex2: string, t: number): string {
    const parse = (hex: string) => [
        Number.parseInt(hex.slice(1, 3), 16),
        Number.parseInt(hex.slice(3, 5), 16),
        Number.parseInt(hex.slice(5, 7), 16),
    ];
    const [r1, g1, b1] = parse(hex1);
    const [r2, g2, b2] = parse(hex2);
    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);
    return `#${r.toString(16).padStart(2, "0")}${g
        .toString(16)
        .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function interpolateGradientColor(
    colorOffsets: ColorOffset[],
    position: number
): string {
    if (colorOffsets.length === 0) {
        return "#ffffff";
    }

    if (position <= colorOffsets[0].offset) {
        return colorOffsets[0].color;
    }

    const lastOffset = colorOffsets.at(-1) as ColorOffset;
    if (position >= lastOffset.offset) {
        return lastOffset.color;
    }

    for (let i = 0; i < colorOffsets.length - 1; i++) {
        if (
            position >= colorOffsets[i].offset &&
            position <= colorOffsets[i + 1].offset
        ) {
            const t =
                (position - colorOffsets[i].offset) /
                (colorOffsets[i + 1].offset - colorOffsets[i].offset);
            return lerpHexColor(
                colorOffsets[i].color,
                colorOffsets[i + 1].color,
                t
            );
        }
    }
    return colorOffsets[0].color;
}

type ColorOffset = {
    offset: number;
    color: string;
};

interface TemperatureBarProps {
    min: number;
    max: number;
    value: number | null;
    units?: [Unit, Unit?];
    colorOffsets: ColorOffset[];
    labels?: Percentage[];
}

const DESIGN_WIDTH = 436;
const BAR_LEFT = 0;
const BAR_RIGHT = 436;
const BAR_HEIGHT = 6.1;
const BAR_RADIUS = 8;

const CardBar = ({
    min,
    max,
    value,
    colorOffsets,
    labels,
    units = ["°C"],
}: TemperatureBarProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { boxWidth: width } = useChartWidth({
        ref: containerRef,
        occupiedWidth: 0,
    });

    // Scale all coordinates to current width
    const scale = width / DESIGN_WIDTH;
    const chartHeight = 40;
    const barHeight = BAR_HEIGHT;
    const barTop = chartHeight / 2 - barHeight / 2;
    const barLeft = BAR_LEFT * scale;
    const barWidth = (BAR_RIGHT - BAR_LEFT) * scale;
    const barRadius = BAR_RADIUS;
    const defaultUnit = units[0];

    // Use labels from props if provided, otherwise fallback to [0, 1]
    const labelPositions =
        Array.isArray(labels) && labels.length > 0 ? labels : [0, 1];

    // Font for labels
    const labelFont = `${12}px sans-serif`;
    const labelsText = [];
    const labelTopPosition = barTop - 15;
    const labelBottomPosition = barTop + barHeight + 6.5;

    const generateLabels = (top: number, unit: Unit) => {
        return labelPositions.map((p, i) => {
            const v = Math.round(min + (max - min) * p);
            const text = typeof unit === "function" ? unit(v) : `${v}${unit}`;
            const textWidth = measureTextWidth(text, labelFont);
            let left;
            if (i === 0) {
                left = barLeft;
            } else if (i === labelPositions.length - 1) {
                left = barLeft + barWidth - textWidth;
            } else {
                left = barLeft + barWidth * p - textWidth / 2;
            }
            return { text, left, top };
        });
    };

    if (units[1]) {
        labelsText.push(...generateLabels(labelTopPosition, units[0]));
        labelsText.push(...generateLabels(labelBottomPosition, units[1]));
    } else {
        labelsText.push(...generateLabels(labelBottomPosition, ""));
    }

    // Dynamic marker position for current value
    const hasValue = value !== null;
    const safeValue = hasValue ? Math.max(min, Math.min(max, value)) : 0;
    const percent = hasValue ? (safeValue - min) / (max - min) : 0;
    const markerX = barLeft + barWidth * percent;

    // Compute pointer color from gradient at current position
    const pointerColor = interpolateGradientColor(colorOffsets, percent);

    // SVG marker size
    const SVG_MARKER_WIDTH = 16;
    const SVG_MARKER_HEIGHT = 24;
    const svgMarkerWidth = SVG_MARKER_WIDTH * scale;
    const svgMarkerHeight = SVG_MARKER_HEIGHT;
    const MARKER_OFFSET = 12;
    const markerTop = barTop - MARKER_OFFSET;
    const markerLeft =
        markerX - svgMarkerWidth / 2 < 0 ? 1 : markerX - svgMarkerWidth / 2;
    // Marker polygon points (downward-pointing arrow shape)
    const markerPolygonPoints = [
        [2.78711, 9.79297],
        [0.492188, 6.73242],
        [0.492188, 0.611328],
        [5.08301, 0.611328],
        [5.08301, 6.73242],
        [2.78711, 9.79297],
    ].map(([x, y]) => [x * 1.2, y * 1.2]);
    // State to control value label visibility
    const [showValue, setShowValue] = useState(false);
    const markerGroupId = "temp-marker-group";
    const onEvents = {
        mouseover: () => setShowValue(true),
        mouseout: () => setShowValue(false),
    };

    const option = {
        graphic: [
            // Gradient bar
            {
                type: "rect",
                left: barLeft,
                top: barTop,
                shape: { width: barWidth, height: barHeight, r: barRadius },
                style: {
                    fill: {
                        type: "linear",
                        x: 0,
                        y: 0,
                        x2: 1,
                        y2: 0,
                        colorStops: colorOffsets,
                    },
                    shadowBlur: 8 * scale,
                    shadowOffsetY: 6.1,
                    shadowColor: "rgba(0,0,0,0.10)",
                },
            },
            // Marker group (pointer polygon)
            {
                type: "group",
                id: markerGroupId,
                left: markerLeft,
                top: markerTop,
                invisible: !hasValue,
                children: [
                    {
                        type: "polygon",
                        shape: { points: markerPolygonPoints },
                        style: { fill: pointerColor, stroke: "none" },
                        invisible: !hasValue,
                        z: 10,
                        onmouseover: onEvents.mouseover,
                        onmouseout: onEvents.mouseout,
                    },
                ],
            },
            // Hover value label
            {
                type: "text",
                left: markerLeft + svgMarkerWidth / 2 - 4 * scale,
                top: markerTop - svgMarkerHeight / 2 + 8,
                invisible: !hasValue,
                style: {
                    text: `${safeValue}${defaultUnit}`,
                    fill: pointerColor,
                    font: `bold ${12}px sans-serif`,
                    textAlign: "left",
                    textVerticalAlign: "bottom",
                    opacity: showValue ? 1 : 0,
                },
                z: 11,
            },
            ...labelsText.map((l) => ({
                type: "text",
                left: l.left,
                top: l.top,
                style: {
                    text: l.text,
                    fill: "#7B8DB0",
                    font: labelFont,
                    opacity: 0.72,
                },
            })),
        ],
        animation: false,
        tooltip: { show: false },
        xAxis: { show: false },
        yAxis: { show: false },
        grid: { left: 0, right: 0, top: 0, bottom: 0 },
    };

    return (
        <div
            ref={containerRef}
            style={{ width: "100%", position: "relative", padding: "0" }}
        >
            <ReactECharts
                option={option}
                style={{
                    width: "100%",
                    height: chartHeight,
                    background: "transparent",
                }}
            />
        </div>
    );
};

export default CardBar;
